import * as React from "react"
import { Link } from "react-router-dom"
import { Flow, type LeafAgingSummary, ReportKind } from "@digits-graphql/frontend/graphql-bearer"
import { buildZeroMoneyFlow } from "@digits-shared/helpers/moneyFlowHelper"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import {
  AGING_COLUMN_OFFSET,
  columnsByComponentSize,
  DUE_DATE,
  INVOICE_DATE,
  LEAF_AGING_AMOUNT_COLUMNS,
} from "src/frontend/components/Shared/Layout/Components/Statements/AgingReport/AgingColumns"
import { RowStyled } from "src/frontend/components/Shared/Layout/Components/Statements/shared"
import {
  useStatementComponentSize,
  useStatementKind,
} from "src/frontend/components/Shared/Layout/Components/Statements/StatementContext"
import { Amount } from "src/frontend/components/Shared/Layout/Components/Statements/StatementRowShared"
import { type RowDetails } from "src/frontend/components/Shared/Layout/Components/Statements/toDetailsData"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"

export interface LeafAgingSummaryComponentProps {
  rowId: string
  depth: number
  summary: LeafAgingSummary
  details?: RowDetails
}

const RowWrapper = styled.div<{ $paddingLeft: number; $columnSpan: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columnSpan}, minmax(0, 1fr));
  grid-column: span ${(props) => props.$columnSpan};
  padding-left: ${(props) => props.$paddingLeft}px;
`

const InvoiceDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${AGING_COLUMN_OFFSET}, minmax(0, 1fr));
  grid-column: span ${AGING_COLUMN_OFFSET};
  font-size: 10px;
  text-align: left;
  padding-left: 32px;
  padding-right: 0;
`

const InvoiceNumber = styled.div`
  padding: 0 8px;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 80px;
  white-space: nowrap;
`

const StyledRow = styled(RowStyled)`
  padding-right: 0;
  padding-top: 6px;
  border-bottom: 1px solid ${colors.secondary10};

  &:hover {
    background: ${colors.secondary10};
  }
`

const InvoiceDate = styled.div`
  padding: 0 8px;
`

const DueDate = styled.div`
  padding: 0 8px;
`

const useDetailsUrl = ({ kind, rowId }: { kind?: ReportKind; rowId: string }) => {
  const pathGenerator = useFrontendPathGenerator()

  return kind === ReportKind.APAging
    ? pathGenerator(routes.billDetails, { billId: rowId })
    : pathGenerator(routes.sentInvoiceDetails, { id: rowId })
}

const LinkGridComponent = ({
  children,
  hasLink,
  rowId,
  kind,
}: {
  children: React.ReactNode
  hasLink: boolean
  kind?: ReportKind
  rowId: string
}) => {
  const detailsUrl = useDetailsUrl({ kind, rowId })
  if (hasLink) {
    return (
      <InvoiceDetailsGrid as={Link} to={detailsUrl}>
        {children}
      </InvoiceDetailsGrid>
    )
  }
  return <InvoiceDetailsGrid>{children}</InvoiceDetailsGrid>
}

const LinkComponent = ({
  children,
  hasLink,
  rowId,
  kind,
}: {
  children: React.ReactNode
  hasLink: boolean
  kind?: ReportKind
  rowId: string
}) => {
  const detailsUrl = useDetailsUrl({ kind, rowId })
  if (hasLink) {
    return <Link to={detailsUrl}>{children}</Link>
  }
  return <div>{children}</div>
}

export const LeafAgingSummaryComponent: React.FC<LeafAgingSummaryComponentProps> = ({
  rowId,
  depth,
  summary: {
    referenceNumber,
    amounts,
    invoiceDate,
    dueDate,
    link: { hasLink },
  },
}) => {
  const kind = useStatementKind()
  const componentSize = useStatementComponentSize()
  const columns = columnsByComponentSize(componentSize) ?? []

  if (componentSize.isExtraLarge || componentSize.isLarge) {
    return (
      <StyledRow id={rowId} depth={depth}>
        <LinkGridComponent hasLink={hasLink} rowId={rowId} kind={kind}>
          <InvoiceDate>{dayjs.unix(invoiceDate).utc().format("MMM DD, YYYY")}</InvoiceDate>
          <DueDate>{dayjs.unix(dueDate).utc().format("MMM DD, YYYY")}</DueDate>
          <InvoiceNumber title={referenceNumber || ""}>{referenceNumber}</InvoiceNumber>
        </LinkGridComponent>
        {LEAF_AGING_AMOUNT_COLUMNS.map((column, i) => (
          <Amount
            rowId={rowId}
            key={`${rowId}-${column}`}
            moneyFlow={
              amounts.find((amount) => amount.column === column)?.moneyFlow ??
              buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
            }
            zeroValueText="-"
          />
        ))}
        <Amount
          rowId={rowId}
          moneyFlow={
            amounts[0]?.moneyFlow ??
            buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
          }
          zeroValueText="-"
        />
      </StyledRow>
    )
  }
  if (componentSize.isMedium) {
    return (
      <StyledRow
        id={rowId}
        depth={depth}
        css={`
          grid-template-columns: repeat(${columns.length}, minmax(0, 1fr));
        `}
      >
        <RowWrapper $columnSpan={columns.length} $paddingLeft={28}>
          {columns.includes(INVOICE_DATE) && (
            <LinkComponent hasLink={hasLink} rowId={rowId} kind={kind}>
              <InvoiceDate>{dayjs.unix(invoiceDate).utc().format("MMM DD, YYYY")}</InvoiceDate>
            </LinkComponent>
          )}
          {columns.includes(DUE_DATE) && (
            <LinkComponent hasLink={hasLink} rowId={rowId} kind={kind}>
              <DueDate>{dayjs.unix(dueDate).utc().format("MMM DD, YYYY")}</DueDate>
            </LinkComponent>
          )}
          <Amount
            rowId={rowId}
            moneyFlow={
              amounts[0]?.moneyFlow ??
              buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
            }
          />
        </RowWrapper>
      </StyledRow>
    )
  }

  return null
}
